import React, {FC, ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, makeStyles, useTheme} from '@material-ui/core';
import Tile from './Tile';
import SupportTile from './SupportTile';
import {useWindowSize} from '@utils';
import {WindowSize} from '@types';
import {useWebConfigContext} from '@context';

interface StyleProps {
    mode: 'lg' | 'md' | 'sm'
    cols: number
}

const useStyles = makeStyles(theme => ({
    root: {
        display: 'grid',
        gap: theme.spacing(3.25),
        gridTemplateColumns: (props: StyleProps) => `repeat(${props.cols}, minmax(${theme.typography.pxToRem(180)}, 1fr))`,
        gridAutoRows: `minmax(${theme.typography.pxToRem(180)}, 1fr)`,
        gridAutoFlow: 'dense'
    },
}));

const SourceGrid: FC = (): ReactElement => {
    const theme = useTheme();
    const {sourcesCols, sources} = useWebConfigContext();

    const getStyleProps = useCallback(({width}: WindowSize): StyleProps => {
        switch (true) {
            case width > theme.breakpoints.values.lg:
                return {mode: 'lg', cols: sourcesCols.lg};
            case width <= theme.breakpoints.values.lg && width > theme.breakpoints.values.md:
                return {mode: 'md', cols: sourcesCols.md};
            default:
                return {mode: 'sm', cols: sourcesCols.sm};
        }
    }, [theme, sourcesCols]);

    const windowSize = useWindowSize();
    const [styleProps, setStyleProps] = useState<StyleProps>(() => getStyleProps(windowSize));
    const classes = useStyles(styleProps);

    useEffect(() => {
        setStyleProps(getStyleProps(windowSize));
    }, [windowSize, getStyleProps]);

    const renderTiles = useMemo(() => {
        const {mode} = styleProps;
        const tiles = [...sources]
            .sort((t1, t2) => (t1.order[mode] || 0) - (t2.order[mode] || 0));

        return <>
            {tiles
                .map((tile) => (
                        tile.support
                            ? (
                                <SupportTile
                                    key={tile.key}
                                    url={tile.url}
                                    cols={tile.cols}
                                    rows={tile.rows}
                                    color={tile.color}/>
                            )
                            : (
                                <Tile
                                    key={tile.key}
                                    label={tile.label}
                                    icon={tile.icon as string}
                                    url={tile.url}
                                    cols={tile.cols}
                                    rows={tile.rows}
                                    color={tile.color}/>
                            )
                    )
                )
            }
        </>;
    }, [styleProps, sources]);

    return (
        <Box className={classes.root}>
            {renderTiles}
        </Box>
    );
};

export default SourceGrid;
